import { useCallback, useEffect, useRef, useState } from 'react';
import type { OfferList, TenantDashboard } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

interface DashboardData {
  dashboard: TenantDashboard;
  /** `null` while offers are loading, or when that request failed. */
  offers: OfferList | null;
}

/** The offers load. A failure leaves the dashboard up. */
export interface OffersPart {
  status: 'loading' | 'ready' | 'error';
  error: unknown;
  reload: () => void;
}

export interface DashboardState {
  /** The dashboard. Offers have their own status. */
  status: 'loading' | 'ready' | 'error';
  data: DashboardData | null;
  error: ApiError | null;
  reload: () => void;
  offers: OffersPart;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/**
 * Loads the dashboard and the offers beside it. An offers failure leaves the dashboard up
 * (TMMRSL-0055). StrictMode-safe; `reload()` refetches the dashboard, `offers.reload` the offers.
 */
export function useDashboard(params?: { from?: string; to?: string }): DashboardState {
  const [status, setStatus] = useState<DashboardState['status']>('loading');
  const [dashboard, setDashboard] = useState<TenantDashboard | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [offers, setOffers] = useState<OfferList | null>(null);
  const [offersStatus, setOffersStatus] = useState<OffersPart['status']>('loading');
  const [offersError, setOffersError] = useState<unknown>(null);
  const api = useTenantApi();
  const started = useRef(false);
  const offersStarted = useRef(false);
  const lastRange = useRef<string | null>(null);
  const from = params?.from;
  const to = params?.to;
  const rangeKey = `${from ?? ''}|${to ?? ''}`;

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    const query =
      from === undefined && to === undefined
        ? undefined
        : {
            ...(from !== undefined ? { from } : {}),
            ...(to !== undefined ? { to } : {}),
          };
    api.getDashboard(query).then(
      (next) => {
        setDashboard(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setDashboard(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api, from, to]);

  const reloadOffers = useCallback(() => {
    setOffersStatus('loading');
    setOffersError(null);
    api.getOffers().then(
      (next) => {
        setOffers(next);
        setOffersError(null);
        setOffersStatus('ready');
      },
      (err: unknown) => {
        setOffers(null);
        setOffersError(err);
        setOffersStatus('error');
      },
    );
  }, [api]);

  useEffect(() => {
    if (started.current && lastRange.current === rangeKey) return;
    started.current = true;
    lastRange.current = rangeKey;
    reload();
  }, [reload, rangeKey]);

  useEffect(() => {
    if (offersStarted.current) return;
    offersStarted.current = true;
    reloadOffers();
  }, [reloadOffers]);

  const data: DashboardData | null = dashboard ? { dashboard, offers } : null;

  return {
    status,
    data,
    error,
    reload,
    offers: { status: offersStatus, error: offersError, reload: reloadOffers },
  };
}
